import { lazy, startTransition, Suspense, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { faXmark } from '@fortawesome/pro-light-svg-icons';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';
import { faClipboardListCheck } from '@fortawesome/pro-regular-svg-icons/faClipboardListCheck';
import { faCompress } from '@fortawesome/pro-regular-svg-icons/faCompress';
import { faGarageCar } from '@fortawesome/pro-regular-svg-icons/faGarageCar';
import { faList } from '@fortawesome/pro-regular-svg-icons/faList';
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons/faMagnifyingGlass';
import { faRotateRight } from '@fortawesome/pro-regular-svg-icons/faRotateRight';
import { faSealExclamation } from '@fortawesome/pro-regular-svg-icons/faSealExclamation';
import { faVanShuttle } from '@fortawesome/pro-regular-svg-icons/faVanShuttle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { InputRef } from 'antd';
import {
  Alert,
  Badge,
  Button,
  DatePicker,
  Flex,
  Popover,
  Segmented,
  Switch,
  theme,
  Tooltip,
  Typography,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import { LocationSelect } from 'pos-client/components/locations/LocationSelect.tsx';
import { dateFormat } from 'pos-client/components/scheduleBoard/constants/boardConfig.ts';
import { useScheduleBoardContext } from 'pos-client/components/scheduleBoard/context/ScheduleBoardContext.tsx';
import { useUserAssignedLocations } from 'pos-client/components/scheduleBoard/hooks/useUserAssignedLocations.ts';
import { isScheduleBoardJob } from 'pos-client/components/scheduleBoard/types/schedulerTypeAdapters.ts';
import { PosInputSearch } from 'pos-client/components/shared/Form/Search/PosSearchInput.tsx';
import {
  datetimeAtTimezone,
  isValidDateInput,
} from 'pos-client/utils/dateHelpers.ts';
import { trpc } from 'pos-client/utils/trpc.ts';
import type { LocationModel } from 'pos-shared/models/locationSchema.ts';
import type { NonTradingDatesListModel } from 'pos-shared/models/openingHoursGroupSchema.ts';
import type { ServiceTypesRequestModel } from 'pos-shared/models/schedulingSchema.ts';

import styles from './ScheduleBoardHeaderPanel.module.css';

const LegendModal = lazy(() =>
  import('./modals/LegendModal.tsx').then((m) => ({ default: m.LegendModal }))
);
const SchedulingConflictsModal = lazy(() =>
  import('./modals/SchedulingConflictsModal.tsx').then((m) => ({
    default: m.SchedulingConflictsModal,
  }))
);

interface ScheduleBoardHeaderPanelProps {
  nonTradingDates: NonTradingDatesListModel;
  resourceDataIsLoading: boolean;
  laneConflictsLoading: boolean;
  boardIsLoading: boolean;
  hasLanes: boolean;
  mode?: 'scheduler' | 'workshop';
}

export const ScheduleBoardHeaderPanel = ({
  nonTradingDates,
  resourceDataIsLoading,
  laneConflictsLoading,
  boardIsLoading,
  hasLanes,
  mode = 'scheduler',
}: ScheduleBoardHeaderPanelProps) => {
  const isSchedulerMode = mode === 'scheduler';
  const { token } = theme.useToken();
  const { t } = useTranslation();

  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
  const [searchPopoverOpen, setSearchPopoverOpen] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const searchInputRef = useRef<InputRef>(null);

  const {
    isScheduleBoardHeaderActionLoading,
    laneConflicts,
    selectedLocation,
    selectedDate,
    serviceTypeFilter,
    isNonTradingDate,
    selectedScheduleId,
    shouldPublishCrmEvent,
    compactView,
    boardData,
    setSelectedLocation,
    setSelectedDate,
    setServiceTypeFilter,
    setBoardData,
    setSearchTerm,
    setIsScheduleBoardHeaderActionLoading,
    setSelectedScheduleId,
    setShouldPublishCrmEvent,
    setCompactView,
    serviceLocationTimezone,
  } = useScheduleBoardContext();

  const [showNoLocationsAlert, setShowNoLocationAlert] =
    useState<boolean>(false);
  const [activeDatePickerButton, setActiveDatePickerButton] =
    useState<number>(0);

  const [showConflictsModal, setShowConflictsModal] = useState(false);

  const trpcContext = trpc.useUtils();

  const handleRefreshBoard = () => {
    trpcContext.getConflictLanes.invalidate();
    trpcContext.getAllNonTradingDates.invalidate();
    trpcContext.getAllTradingTimes.invalidate();
    trpcContext.getResourcesByLocationId.invalidate();
    trpcContext.findScheduleBoardJobsAndBlockedSlots.invalidate();

    if (selectedScheduleId) setSelectedScheduleId(null);
  };

  const serviceTypesBasedOnLocation = (selectedLocation: LocationModel) => {
    const serviceTypesArr: ServiceTypesRequestModel[] = [];

    if (selectedLocation && selectedLocation.serviceTypes.includes('Static')) {
      serviceTypesArr.push('Static');
    }

    if (selectedLocation && selectedLocation.serviceTypes.includes('Mobile')) {
      serviceTypesArr.push('Mobile');
    }

    if (serviceTypesArr.length === 0) {
      serviceTypesArr.push('Static');
    }

    return serviceTypesArr;
  };

  const handleLocationSelectChange = (
    selectedServiceLocationId: string,
    data: LocationModel[]
  ) => {
    if (data.length === 1 && data[0].isActive === false) return;
    setActiveDatePickerButton(0);
    const selectedLocation = data?.find(
      (location) => location.id === selectedServiceLocationId
    );

    const serviceTypes = selectedLocation
      ? serviceTypesBasedOnLocation(selectedLocation)
      : undefined;
    if (selectedLocation && serviceTypes) {
      setServiceTypeFilter(serviceTypes[0]);
    }
    if (selectedLocation) {
      setSelectedLocation(selectedLocation);
    }
    if (selectedScheduleId) setSelectedScheduleId(null);

    setIsScheduleBoardHeaderActionLoading(true);
  };

  const handleLegendChange = () => {
    setModalIsOpen((prevState) => !prevState);
  };

  const { locations, isLoadingLocations } = useUserAssignedLocations({
    setShowNoLocationAlert,
    handleLocationSelectChange,
  });

  const activeLocations = useMemo(
    () => locations.filter((l) => l.isActive),
    [locations]
  );

  const handleDayChange = (days: number) => {
    startTransition(() => {
      setSelectedDate(
        (prevDate) => new Date(prevDate.getTime() + days * 24 * 60 * 60 * 1000)
      );
    });

    if (selectedScheduleId) setSelectedScheduleId(null);
  };

  const handleServiceTypeChange = (
    selectedOption: ServiceTypesRequestModel
  ) => {
    setIsScheduleBoardHeaderActionLoading(true);
    setActiveDatePickerButton(0);
    setServiceTypeFilter(selectedOption);
    setBoardData([]);
  };

  const disabledDates = (date: dayjs.Dayjs) => {
    const locationIsClosed = nonTradingDates?.some((item) =>
      dayjs(date).isSame(new Date(item.date), 'day')
    );

    return !!locationIsClosed;
  };

  const resolveTimezoneNow = () => {
    const fromTz = datetimeAtTimezone(serviceLocationTimezone).toDate();
    return isValidDateInput(fromTz) ? fromTz : new Date();
  };

  const handleDatePickerChange = (date: Dayjs | null) => {
    setIsScheduleBoardHeaderActionLoading(true);

    if (!date) {
      startTransition(() => {
        setSelectedDate(resolveTimezoneNow());
      });
      setActiveDatePickerButton(0);
      return;
    }

    startTransition(() => {
      if (date.isSame(dayjs(), 'seconds')) {
        setSelectedDate(resolveTimezoneNow());
      } else {
        const tzAdjusted = date.tz(serviceLocationTimezone).toDate();
        setSelectedDate(isValidDateInput(tzAdjusted) ? tzAdjusted : new Date());
      }
    });

    setActiveDatePickerButton(0);
  };

  const handleDayChangeAction = (action: 'forward' | 'today' | 'back') => {
    const now = resolveTimezoneNow();
    const willChangeDate =
      action !== 'today' ||
      !dayjs(selectedDate)
        .tz(serviceLocationTimezone)
        .isSame(dayjs(now).tz(serviceLocationTimezone), 'day');

    if (willChangeDate) {
      setIsScheduleBoardHeaderActionLoading(true);
    }

    const actions = {
      forward: () => {
        handleDayChange(+1);
        setActiveDatePickerButton(3);
      },
      today: () => {
        startTransition(() => {
          setSelectedDate(now);
        });
        setActiveDatePickerButton(2);
      },
      back: () => {
        handleDayChange(-1);
        setActiveDatePickerButton(1);
      },
    };

    actions[action]?.();
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
    setSearchTerm(value);
  };

  const allAcknowledged = useMemo(
    () =>
      (laneConflicts ?? []).every((lane) =>
        lane.conflicts.every((c) => c.acknowledged)
      ),
    [laneConflicts]
  );

  const jobCount = boardData?.filter(isScheduleBoardJob).length ?? 0;
  const isBoardLoading = boardIsLoading || isScheduleBoardHeaderActionLoading;
  let jobCountText: string;
  if (!selectedLocation) {
    jobCountText = t('scheduling.scheduleBoard.jobCount', { count: 0 });
  } else if (isBoardLoading || !hasLanes) {
    jobCountText = `${t('common.states.loading')}...`;
  } else {
    jobCountText = t('scheduling.scheduleBoard.jobCount', { count: jobCount });
  }

  return (
    <>
      <Flex
        vertical
        gap={4}
        style={{
          width: '100%',
          ...(isSchedulerMode && {
            paddingRight: 40,
            marginBottom: token.marginSM,
          }),
        }}
      >
        <Flex
          gap={4}
          align="center"
          wrap="wrap"
          justify="space-between"
          className={styles.headerPanel}
        >
        <Flex gap={4} align="center" wrap="wrap">
          <Button
            type="primary"
            aria-label={t('scheduling.resourceView.legend')}
            icon={<FontAwesomeIcon icon={modalIsOpen ? faXmark : faList} />}
            onClick={handleLegendChange}
          />
          <Popover
            open={searchPopoverOpen}
            onOpenChange={setSearchPopoverOpen}
            afterOpenChange={(open) => {
              if (open) searchInputRef.current?.focus();
            }}
            trigger="click"
            placement="bottomLeft"
            content={
              <div style={{ width: 280 }}>
                <PosInputSearch
                  ref={searchInputRef}
                  allowClear
                  value={searchValue}
                  placeholder={t('common.placeholders.searchBy', {
                    searchBy: t(
                      'scheduling.scheduleBoard.customerNameOrderReferenceOrLicensePlate'
                    ),
                  })}
                  onChange={(e) => handleSearch(e.target.value)}
                />
              </div>
            }
          >
            <Badge dot={searchValue.length > 0} offset={[-4, 4]}>
              <Button
                type={searchValue ? 'primary' : 'default'}
                aria-label={t('common.buttons.search')}
                icon={<FontAwesomeIcon icon={faMagnifyingGlass} />}
              />
            </Badge>
          </Popover>
          <Tooltip title={t('common.buttons.refresh')}>
            <Button
              aria-label={t('common.buttons.refresh')}
              icon={<FontAwesomeIcon icon={faRotateRight} />}
              onClick={handleRefreshBoard}
              disabled={!selectedLocation}
            />
          </Tooltip>
          <Flex gap={4}>
            <Button
              icon={<FontAwesomeIcon icon={faChevronLeft} />}
              disabled={resourceDataIsLoading}
              ghost={resourceDataIsLoading}
              onClick={() => handleDayChangeAction('back')}
              loading={resourceDataIsLoading && activeDatePickerButton === 1}
            />
            <Button
              onClick={() => handleDayChangeAction('today')}
              disabled={resourceDataIsLoading}
              ghost={resourceDataIsLoading}
              loading={resourceDataIsLoading && activeDatePickerButton === 2}
            >
              {t('common.time.timeFrame.today')}
            </Button>
            <Button
              icon={<FontAwesomeIcon icon={faChevronRight} />}
              onClick={() => handleDayChangeAction('forward')}
              disabled={resourceDataIsLoading}
              ghost={resourceDataIsLoading}
              loading={resourceDataIsLoading && activeDatePickerButton === 3}
            />
          </Flex>

          <DatePicker
            value={dayjs(selectedDate)}
            onChange={handleDatePickerChange}
            allowClear={false}
            defaultValue={dayjs(selectedDate)}
            format={dateFormat}
            style={{ minWidth: 176, width: 176 }}
            disabledDate={disabledDates}
            disabled={resourceDataIsLoading}
          />

          {isSchedulerMode && (
            <Flex
              gap={6}
              align="center"
              style={{ whiteSpace: 'nowrap', padding: '0 12px' }}
            >
              <FontAwesomeIcon
                icon={faClipboardListCheck}
                color={token.colorPrimary}
              />
              <Typography.Text strong style={{ color: token.colorPrimary }}>
                {jobCountText}
              </Typography.Text>
            </Flex>
          )}

          {isSchedulerMode && (
            <Tooltip
              title={
                shouldPublishCrmEvent
                  ? t('scheduling.scheduleBoard.publishCrmEvent.enabled')
                  : t('scheduling.scheduleBoard.publishCrmEvent.suppressed')
              }
            >
              <Switch
                checked={shouldPublishCrmEvent === false}
                aria-label={t('scheduling.scheduleBoard.sccShort')}
                checkedChildren={t('scheduling.scheduleBoard.sccShort')}
                unCheckedChildren={t('scheduling.scheduleBoard.sccShort')}
                onChange={(checked) => setShouldPublishCrmEvent(!checked)}
              />
            </Tooltip>
          )}
          {isSchedulerMode && (
            <Tooltip title={t('scheduling.scheduleBoard.compactView')}>
              <Flex gap={6} align="center">
                <FontAwesomeIcon
                  icon={faCompress}
                  style={{ marginLeft: 4 }}
                />
                <Switch
                  checked={compactView}
                  aria-label={t('scheduling.scheduleBoard.compactView')}
                  checkedChildren={t('common.states.on')}
                  unCheckedChildren={t('common.states.off')}
                  onChange={(checked) => setCompactView(checked)}
                />
              </Flex>
            </Tooltip>
          )}
        </Flex>
        <Flex gap={4} align="center">
            {!laneConflictsLoading &&
              !isEmpty(laneConflicts) &&
              (() => {
                const conflictButtonStyle = allAcknowledged
                  ? {
                      backgroundColor: token.colorBgContainer,
                      borderColor: token.colorPrimary,
                      color: token.colorPrimary,
                    }
                  : {
                      backgroundColor: token.colorError,
                      borderColor: token.colorError,
                      color: token.colorWhite,
                    };
                const conflictButton = (
                  <Button
                    aria-label={t('scheduling.conflictLanes.viewConflicts')}
                    onClick={() => setShowConflictsModal(true)}
                    icon={
                      <FontAwesomeIcon
                        icon={faSealExclamation}
                        color={
                          allAcknowledged
                            ? token.colorPrimary
                            : token.colorWhite
                        }
                      />
                    }
                    style={conflictButtonStyle}
                  />
                );
                if (showConflictsModal) return conflictButton;
                return (
                  <Tooltip
                    trigger={['hover']}
                    title={
                      allAcknowledged
                        ? `${t('scheduling.conflictLanes.allAcknowledgedHeader')} ${t(
                            'scheduling.conflictLanes.allAcknowledgedMessage',
                            { location: selectedLocation?.name }
                          )}`
                        : t('scheduling.conflictLanes.warningConflictLane', {
                            location: selectedLocation?.name,
                          })
                    }
                  >
                    {conflictButton}
                  </Tooltip>
                );
              })()}
            {selectedLocation && (
              <Segmented
                options={serviceTypesBasedOnLocation(selectedLocation).map(
                  (type) => ({
                    value: type,
                    label: (
                      <Tooltip
                        placement="top"
                        title={
                          type === 'Static'
                            ? t('scheduling.scheduleBoard.static')
                            : t('scheduling.scheduleBoard.mobile')
                        }
                      >
                        <FontAwesomeIcon
                          icon={
                            type === 'Static' ? faGarageCar : faVanShuttle
                          }
                        />
                      </Tooltip>
                    ),
                  })
                )}
                value={serviceTypeFilter}
                onChange={(selectedOption) =>
                  handleServiceTypeChange(
                    selectedOption as ServiceTypesRequestModel
                  )
                }
                style={{ width: 'fit-content' }}
              />
            )}
            <LocationSelect
              id="serviceLocationSelect"
              className={styles.locationSelect}
              disabled={locations.length <= 1}
              value={selectedLocation?.id}
              showInactiveLocations={false}
              onChange={(value) => handleLocationSelectChange(value, locations)}
            />
          </Flex>
        </Flex>
      </Flex>
      {!isLoadingLocations && activeLocations.length < 1 && (
        <Alert
          message={t('scheduling.scheduleBoard.noActiveLocationsError')}
          type="warning"
        />
      )}

      {showNoLocationsAlert && (
        <Alert
          message={t('scheduling.scheduleBoard.userNotAssignedToLocation')}
          type="warning"
        />
      )}
      {!selectedLocation &&
        !showNoLocationsAlert &&
        activeLocations.length > 0 && (
          <Alert
            message={
              isLoadingLocations
                ? `${t('common.states.loadingField', { field: t('locations.serviceLocations') })}...`
                : t('scheduling.scheduleBoard.pleaseSelectServiceLocation')
            }
            type="warning"
            style={
              isSchedulerMode
                ? { marginBottom: token.marginSM }
                : { marginTop: token.marginSM }
            }
          />
        )}
      {isNonTradingDate && (
        <Alert
          message={t('scheduling.scheduleBoard.nonTradingDateError')}
          type="warning"
        />
      )}
      {showConflictsModal && (
        <Suspense fallback={null}>
          <SchedulingConflictsModal
            conflictData={laneConflicts ?? []}
            modalIsOpen={showConflictsModal}
            handleCloseModal={() => setShowConflictsModal(false)}
          />
        </Suspense>
      )}
      {modalIsOpen && (
        <Suspense fallback={null}>
          <LegendModal handleCloseModal={() => setModalIsOpen(false)} />
        </Suspense>
      )}
    </>
  );
};
